import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Paperclip, X, FileText, Image, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface FileUploadProps {
  onFileUploaded: (url: string, name: string, type: string) => void;
  disabled?: boolean;
}

const ALLOWED_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "application/pdf",
];

const MAX_SIZE = 10 * 1024 * 1024; // 10MB

export function FileUpload({ onFileUploaded, disabled }: FileUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error("Format non supporté. Utilisez PNG, JPEG ou PDF.");
      return;
    }

    if (file.size > MAX_SIZE) {
      toast.error("Fichier trop volumineux. Maximum 10MB.");
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);

    try {
      const fileExt = selectedFile.name.split(".").pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("attachments")
        .upload(filePath, selectedFile);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("attachments")
        .getPublicUrl(filePath);

      onFileUploaded(publicUrl, selectedFile.name, selectedFile.type);
      setSelectedFile(null);
      if (inputRef.current) inputRef.current.value = "";
      toast.success("Fichier envoyé");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Échec de l'envoi du fichier");
    } finally {
      setUploading(false);
    }
  };

  const clearSelection = () => {
    setSelectedFile(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const isImage = selectedFile?.type.startsWith("image/");

  return (
    <div className="flex items-center gap-2">
      <input
        ref={inputRef}
        type="file"
        accept=".png,.jpg,.jpeg,.webp,.pdf"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled || uploading}
      />

      {selectedFile ? (
        <div className="flex items-center gap-2 bg-muted px-3 py-1.5 rounded-lg">
          {isImage ? (
            <Image size={16} className="text-primary" />
          ) : (
            <FileText size={16} className="text-primary" />
          )}
          <span className="text-sm truncate max-w-[120px]">{selectedFile.name}</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={clearSelection}
            disabled={uploading}
          >
            <X size={14} />
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handleUpload}
            disabled={uploading}
            className="h-7"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : "Envoyer"}
          </Button>
        </div>
      ) : (
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
        >
          <Paperclip size={20} className="text-muted-foreground" />
        </Button>
      )}
    </div>
  );
}
